import { ReactNode } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useAlly } from "../../context/ally";
import m, { desktop } from "../../styles/molanturTheme";
import logo from "../../assets/ally/logo/imagotipo-beige.svg";
import { Icon, IconButton } from "./ui";

export type AllySection = 'guests' | 'reservations' | 'settlements' | 'profile'

// `short` es la etiqueta de las píldoras del celular, donde las tres tienen que caber en una fila.
const NAV: { key: AllySection, label: string, short: string, icon: string, to: string }[] = [
    { key: 'guests', label: 'Mis huéspedes', short: 'Huéspedes', icon: 'users', to: '/ally' },
    { key: 'reservations', label: 'Mis reservas', short: 'Reservas', icon: 'calendar', to: '/ally/me?tab=reservations' },
    { key: 'settlements', label: 'Liquidaciones', short: 'Liquidaciones', icon: 'wallet', to: '/ally/me?tab=settlements' },
]

// Marco del portal de aliados: en celular, encabezado oscuro con el logo y la navegación en
// píldoras; desde 900px, barra lateral fija. El contenido de cada pantalla va como children.
export default function Shell({ active, children }: { active: AllySection, children: ReactNode }) {
    const { user, setUser } = useAlly()
    const navigate = useNavigate()
    const name = user?.user?.name || ''

    function logout() {
        localStorage.removeItem('ally')
        setUser(null)
        navigate('/ally/access')
    }

    const nav = NAV.map(item => (
        <button key={item.key} type="button" className="nav-item" aria-label={item.label} aria-current={active === item.key ? 'page' : undefined} onClick={() => navigate(item.to)}>
            <span className="nav-icon"><Icon name={item.icon} size={18} stroke={2} /></span>
            <span className="long">{item.label}</span>
            <span className="short">{item.short}</span>
        </button>
    ))

    return (
        <Main>
            <aside className="sidebar">
                <div className="logo"><img src={logo} alt="Molantur Atacama Tours" /></div>
                <nav aria-label="Secciones">{nav}</nav>
                <div className="who">
                    <button type="button" className="me" aria-current={active === 'profile' ? 'page' : undefined} onClick={() => navigate('/ally/me')}>
                        <span className="home"><Icon name="home" size={18} /></span>
                        <span>
                            <span className="tag">Portal de aliados</span>
                            <span className="name">{name}</span>
                        </span>
                    </button>
                    <IconButton name="logout" label="Cerrar sesión" tone="glass" size={40} onClick={logout} />
                </div>
            </aside>

            <header className="topbar">
                <div className="logo"><img src={logo} alt="Molantur Atacama Tours" /></div>
                <div className="who">
                    <button type="button" className="me" onClick={() => navigate('/ally/me')}>
                        <span className="tag">Portal de aliados</span>
                        <span className="name">{name}</span>
                    </button>
                    <IconButton name="logout" label="Cerrar sesión" tone="glass" size={44} onClick={logout} />
                </div>
                <nav aria-label="Secciones">{nav}</nav>
            </header>

            <main>{children}</main>
        </Main>
    )
}

const Main = styled.div`
    min-height: 100dvh;
    background: ${m.color.bg};
    color: ${m.color.ink};
    font-family: ${m.font.text};
    -webkit-font-smoothing: antialiased;

    .logo {
        display: flex;
        justify-content: center;
        border-bottom: 1px solid rgba(230,200,165,0.18);
    }
    .tag {
        display: block;
        font-family: ${m.font.label};
        font-size: 10px;
        letter-spacing: 0.2em;
        text-transform: uppercase;
        color: rgba(255,255,255,0.6);
    }
    .name {
        display: block;
        color: #fff;
    }
    .me {
        border: none;
        background: transparent;
        padding: 0;
        text-align: left;
        cursor: pointer;
        min-width: 0;
    }
    .nav-item {
        display: flex;
        align-items: center;
        gap: 10px;
        border: none;
        cursor: pointer;
        font-family: ${m.font.text};
        font-weight: 600;
        -webkit-tap-highlight-color: transparent;
    }

    /* ── Celular ── */
    .sidebar { display: none; }
    .topbar .nav-item .long { display: none; }
    .topbar {
        background: ${m.color.ink};
        padding: 14px 18px 16px;
        border-radius: 0 0 28px 28px;
        .logo {
            padding: 4px 0 14px;
            margin-bottom: 14px;
            img { height: 52px; }
        }
        .who {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
        }
        .name {
            margin-top: 3px;
            font-family: ${m.font.display};
            font-weight: 600;
            font-size: 22px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
        nav {
            display: flex;
            gap: 6px;
            margin-top: 14px;
            overflow-x: auto;
            scrollbar-width: none;
            &::-webkit-scrollbar { display: none; }
        }
        .nav-item {
            /* Cada píldora mide al menos lo que su texto: si no caben las tres, la fila se desliza
               en vez de montarse una sobre otra. */
            flex: 1 0 auto;
            justify-content: center;
            white-space: nowrap;
            min-height: 44px;
            padding: 0 12px;
            border-radius: ${m.radius.pill};
            background: rgba(255,255,255,0.12);
            color: rgba(255,255,255,0.85);
            font-size: 13px;
            gap: 7px;
        }
        .nav-item[aria-current="page"] {
            background: ${m.color.sol};
            color: ${m.color.ink};
        }
        /* Celulares angostos: sin iconos, para que las tres secciones quepan sin deslizar. */
        @media (max-width: 399px) {
            .nav-icon { display: none; }
            .nav-item { padding: 0 10px; }
        }
        @media (max-width: 359px) {
            nav { gap: 5px; }
            .nav-item {
                padding: 0 7px;
                font-size: 12.5px;
            }
        }
    }
    main {
        padding: 16px 16px 40px;
    }

    /* ── Desktop ── */
    ${desktop} {
        display: grid;
        grid-template-columns: 256px minmax(0, 1fr);
        .topbar { display: none; }
        .sidebar {
            position: sticky;
            top: 0;
            height: 100dvh;
            display: flex;
            flex-direction: column;
            gap: 6px;
            padding: 28px 18px;
            background: ${m.color.ink};
            .logo {
                padding: 6px 0 26px;
                margin-bottom: 14px;
                img { height: 64px; }
            }
            nav {
                display: flex;
                flex-direction: column;
                gap: 6px;
            }
            .nav-item .short { display: none; }
            .nav-item {
                padding: 11px 14px;
                border-radius: 12px;
                background: transparent;
                color: rgba(255,255,255,0.7);
                font-size: 14.5px;
                gap: 12px;
            }
            .nav-item[aria-current="page"] {
                background: rgba(230,200,165,0.14);
                color: ${m.color.sol};
            }
            .who {
                margin-top: auto;
                padding-top: 16px;
                border-top: 1px solid rgba(230,200,165,0.18);
                display: flex;
                align-items: center;
                gap: 8px;
            }
            .me {
                flex: 1;
                display: flex;
                align-items: center;
                gap: 10px;
                padding: 6px;
                border-radius: 12px;
            }
            .me[aria-current="page"] { background: rgba(230,200,165,0.14); }
            .home {
                width: 36px;
                height: 36px;
                flex-shrink: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 50%;
                background: rgba(255,255,255,0.12);
                color: #fff;
            }
            .name {
                font-weight: 600;
                font-size: 14px;
                margin-top: 2px;
            }
        }
        main {
            width: 100%;
            max-width: 1120px;
            padding: 36px clamp(20px, 4vw, 48px) 60px;
        }
    }
`
